import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isStillHereDue, type StillHerePromptRow } from "@/lib/presence/stillHere";

const POLL_MS = 60_000;
const LIME = "oklch(0.86 0.19 125)";

/**
 * Aviso "Ainda está aqui?" aos 90 minutos de presença, no mesmo estilo das
 * caixas de diálogo do Zappy (layout claro). Só consulta a própria presença
 * (função do servidor restrita ao titular) e nunca lê localização.
 * "Sim, continuo aqui" renova a presença; "Já saí" a encerra.
 */
export function StillHerePrompt() {
  const { user } = useAuth();
  const [row, setRow] = useState<StillHerePromptRow | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setRow(null);
      setOpen(false);
      return;
    }
    const { data, error } = await supabase.rpc("get_still_here_prompt");
    if (error) return;
    const next = (Array.isArray(data) ? data[0] : data) as StillHerePromptRow | undefined;
    setRow(next ?? null);
    setOpen(isStillHereDue(next ?? null, Date.now()));
  }, [user]);

  useEffect(() => {
    void load();
    const onUpdate = () => void load();
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    const id = window.setInterval(() => void load(), POLL_MS);
    window.addEventListener("checkin:updated", onUpdate);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("checkin:updated", onUpdate);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  // Abre exatamente no horário previsto, se o app estiver aberto.
  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (!row || open) return;
    const wait = Date.parse(row.due_at) - Date.now();
    if (!Number.isFinite(wait) || wait <= 0 || wait > 3 * 60 * 60 * 1000) return;
    timerRef.current = window.setTimeout(() => setOpen(isStillHereDue(row, Date.now())), wait + 500);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [row, open]);

  const stay = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc("confirm_still_here");
      const result = data as { ok?: boolean; error?: string } | null;
      if (error || !result?.ok) {
        toast.error(
          result?.error === "presence_expired"
            ? "Sua presença já foi encerrada. Faça um novo check-in no local."
            : "Não foi possível renovar sua presença. Tente novamente.",
        );
        if (result?.error === "presence_expired") setOpen(false);
        return;
      }
      setOpen(false);
      toast.success("Presença renovada");
      window.dispatchEvent(new Event("checkin:updated"));
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc("check_out_current_venue", { _venue_id: null });
      if (error) {
        toast.error("Não foi possível encerrar sua presença. Tente novamente.");
        return;
      }
      setOpen(false);
      setRow(null);
      toast.success("Presença encerrada");
      window.dispatchEvent(new Event("checkin:updated"));
    } finally {
      setBusy(false);
    }
  };

  if (!user || !row || !open) return null;

  const button = { display: "block", width: "100%", minHeight: 52, borderRadius: 99, font: "700 16px Inter, Arial, sans-serif", marginTop: 10, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 } as const;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(10,19,16,.55)", display: "grid", placeItems: "center", padding: 22 }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="still-here-title" aria-describedby="still-here-text" style={{ width: "100%", maxWidth: 380, background: "#F3F5F1", color: "#0E1A15", borderRadius: 26, padding: 22, textAlign: "center", fontFamily: "Inter, Arial, sans-serif" }}>
        <div aria-hidden="true" style={{ width: 60, height: 60, borderRadius: 30, margin: "0 auto 14px", background: `color-mix(in oklch, ${LIME} 16%, transparent)`, display: "grid", placeItems: "center" }}>
          <span style={{ width: 26, height: 26, borderRadius: 13, border: "3px solid oklch(0.62 0.19 125)" }} />
        </div>
        <h2 id="still-here-title" style={{ font: "800 22px 'Space Grotesk', 'Montserrat', sans-serif", margin: "0 0 8px" }}>Ainda está aqui?</h2>
        <p id="still-here-text" style={{ color: "#5B6760", fontSize: 14.5, lineHeight: 1.5, margin: 0 }}>
          Confirme se você continua {row.venue_name ? <>em <b style={{ color: "#0E1A15" }}>{row.venue_name}</b></> : "no estabelecimento"}. Ao confirmar, sua presença é renovada por até 3 horas (ou até o fechamento).
        </p>
        <button type="button" disabled={busy} onClick={() => void stay()} style={{ ...button, marginTop: 18, border: 0, background: LIME, color: "#0A1310" }}>Sim, continuo aqui</button>
        <button type="button" disabled={busy} onClick={() => void leave()} style={{ ...button, border: "1.5px solid #D3DACF", background: "transparent", color: "#0E1A15" }}>Já saí</button>
        <p style={{ fontSize: 12, color: "#5B6760", margin: "12px 0 0" }}>Sua localização não é lida para responder.</p>
      </div>
    </div>
  );
}
